/**
 * CSV Export / Import for GTM Tasks
 *
 * ═══════════════════════════════════════════════════════════════
 *  CSV FORMAT SPECIFICATION v1.0
 * ═══════════════════════════════════════════════════════════════
 *
 *  ENCODING:     UTF-8 with BOM (for Excel compatibility)
 *  DELIMITER:    comma (,)
 *  LINE ENDING:  \r\n (CRLF)
 *  QUOTING:      RFC 4180 — fields containing commas, quotes,
 *                or newlines MUST be enclosed in double-quotes.
 *                Double-quotes inside a field are escaped as "".
 *
 *  COLUMNS (exact order, all required in header):
 *  ──────────────────────────────────────────────────────────────
 *  #  Column         Type        Allowed values / format
 *  ──────────────────────────────────────────────────────────────
 *  1  id             string      Unique ID (e.g. "obj-1"). Required.
 *  2  laneId         enum        objective_kpis | offer_pricing |
 *                                channels | commercial_pipeline |
 *                                weekly_content | revops_tracking |
 *                                feedback_loop
 *  3  title          string      Non-empty, max 200 chars.
 *  4  description    string      Free text. May be empty.
 *  5  status         enum        planned | in_progress | blocked | done
 *  6  owner          enum        Joel | Albert | AI Wave | Joint
 *  7  priority       enum        critical | high | medium | low
 *  8  targetDate     date|empty  ISO 8601 date (YYYY-MM-DD) or empty.
 *  9  completedDate  date|empty  ISO 8601 date or empty.
 * 10  channel        string      Channel name or empty.
 * 11  channelTier    enum|empty  A | B | C or empty.
 * 12  channelType    enum|empty  owned | rented | borrowed or empty.
 * 13  url            string      Valid URL or empty.
 * 14  utmCampaign    string      UTM campaign tag or empty.
 * 15  tags           string      Semicolon-separated (;). E.g. "seo;paid"
 * 16  dependsOn      string      Semicolon-separated card IDs. E.g. "obj-1;ch-2"
 * 17  notes          string      Free text. May be empty.
 * 18  weekNumber     int|empty   0 | 1 | 2 | 3 | 4 or empty.
 *
 *  RULES:
 *  - The header row MUST be present and MUST match column names exactly.
 *  - Column order MUST follow the specification above.
 *  - Multi-value fields (tags, dependsOn) use semicolon (;) as separator.
 *  - Empty optional fields should be empty (not "null" or "undefined").
 *  - Dates MUST be YYYY-MM-DD format (ISO 8601 calendar date).
 *  - No trailing commas. No extra columns.
 *  - Duplicate IDs will cause a validation error.
 *  - Rows with missing required fields (id, laneId, title, status,
 *    owner, priority) will be rejected.
 *
 * ═══════════════════════════════════════════════════════════════
 */

import type { Card, CardStatus, Owner, Priority, LaneId, ChannelTier, ChannelType } from "../data/types";

// ── Column definition ───────────────────────────────────────

const CSV_COLUMNS = [
  "id",
  "laneId",
  "title",
  "description",
  "status",
  "owner",
  "priority",
  "targetDate",
  "completedDate",
  "channel",
  "channelTier",
  "channelType",
  "url",
  "utmCampaign",
  "tags",
  "dependsOn",
  "notes",
  "weekNumber",
] as const;

const VALID_LANE_IDS: Set<string> = new Set([
  "objective_kpis", "offer_pricing", "channels",
  "commercial_pipeline", "weekly_content", "revops_tracking", "feedback_loop",
]);
const VALID_STATUSES: Set<string> = new Set(["planned", "in_progress", "blocked", "done"]);
const VALID_OWNERS: Set<string> = new Set(["Joel", "Albert", "AI Wave", "Joint"]);
const VALID_PRIORITIES: Set<string> = new Set(["critical", "high", "medium", "low"]);
const VALID_TIERS: Set<string> = new Set(["A", "B", "C"]);
const VALID_CHANNEL_TYPES: Set<string> = new Set(["owned", "rented", "borrowed"]);
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const V2_TAG_PREFIXES = ["task:", "phase:", "stream:", "window:"];

// ── Export ───────────────────────────────────────────────────

function escapeField(value: string): string {
  if (value.includes(",") || value.includes('"') || value.includes("\n") || value.includes("\r")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function exportCardsToCSV(cards: Card[]): string {
  const BOM = "\uFEFF";
  const header = CSV_COLUMNS.join(",");
  const rows = cards.map((card) => {
    const values: string[] = [
      card.id,
      card.laneId,
      card.title,
      card.description,
      card.status,
      card.owner,
      card.priority,
      card.targetDate ?? "",
      card.completedDate ?? "",
      card.channel ?? "",
      card.channelTier ?? "",
      card.channelType ?? "",
      card.url ?? "",
      card.utmCampaign ?? "",
      card.tags.join(";"),
      card.dependsOn.join(";"),
      card.notes,
      card.weekNumber !== undefined ? String(card.weekNumber) : "",
    ];
    return values.map(escapeField).join(",");
  });

  return BOM + [header, ...rows].join("\r\n") + "\r\n";
}

export function downloadCSV(cards: Card[], filename = "gtm-tasks.csv"): void {
  const csv = exportCardsToCSV(cards);
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Import ──────────────────────────────────────────────────

export interface CSVImportResult {
  cards: Card[];
  errors: string[];
  warnings: string[];
}

function parseCSVLine(line: string): string[] {
  const fields: string[] = [];
  let current = "";
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const ch = line[i]!;
    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i += 2;
        } else {
          inQuotes = false;
          i++;
        }
      } else {
        current += ch;
        i++;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
        i++;
      } else if (ch === ",") {
        fields.push(current);
        current = "";
        i++;
      } else {
        current += ch;
        i++;
      }
    }
  }
  fields.push(current);
  return fields;
}

/**
 * Split CSV text into logical lines, respecting quoted fields that may
 * contain newlines. A line boundary inside a double-quoted field is NOT
 * treated as a row separator.
 */
function splitCSVRows(text: string): string[] {
  const rows: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;

    if (inQuotes) {
      if (ch === '"') {
        if (i + 1 < text.length && text[i + 1] === '"') {
          current += '""';
          i++; // skip escaped quote
        } else {
          inQuotes = false;
          current += ch;
        }
      } else {
        current += ch;
      }
    } else {
      if (ch === '"') {
        inQuotes = true;
        current += ch;
      } else if (ch === "\r") {
        // CRLF or CR
        if (i + 1 < text.length && text[i + 1] === "\n") {
          i++; // skip LF
        }
        if (current.trim().length > 0) rows.push(current);
        current = "";
      } else if (ch === "\n") {
        if (current.trim().length > 0) rows.push(current);
        current = "";
      } else {
        current += ch;
      }
    }
  }

  if (current.trim().length > 0) rows.push(current);
  return rows;
}

export function parseCSVToCards(csvText: string): CSVImportResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const cards: Card[] = [];

  // Strip BOM if present
  const text = csvText.replace(/^\uFEFF/, "");
  const lines = splitCSVRows(text);

  if (lines.length === 0) {
    errors.push("CSV file is empty.");
    return { cards, errors, warnings };
  }

  // ── Validate header ──
  const headerFields = parseCSVLine(lines[0]!);
  const headerNormalized = headerFields.map((h) => h.trim());

  if (headerNormalized.length !== CSV_COLUMNS.length) {
    errors.push(
      `Header has ${headerNormalized.length} columns, expected ${CSV_COLUMNS.length}. ` +
      `Required columns: ${CSV_COLUMNS.join(", ")}`
    );
    return { cards, errors, warnings };
  }

  for (let i = 0; i < CSV_COLUMNS.length; i++) {
    if (headerNormalized[i] !== CSV_COLUMNS[i]) {
      errors.push(`Column ${i + 1}: expected "${CSV_COLUMNS[i]}", got "${headerNormalized[i]}".`);
    }
  }

  if (errors.length > 0) {
    return { cards, errors, warnings };
  }

  // ── Parse rows ──
  const seenIds = new Set<string>();

  for (let rowIdx = 1; rowIdx < lines.length; rowIdx++) {
    const rowNum = rowIdx + 1; // 1-indexed for user display
    const fields = parseCSVLine(lines[rowIdx]!);

    if (fields.length !== CSV_COLUMNS.length) {
      errors.push(`Row ${rowNum}: has ${fields.length} columns, expected ${CSV_COLUMNS.length}. Skipped.`);
      continue;
    }

    const [
      id, laneId, title, description, status, owner, priority,
      targetDate, completedDate, channel, channelTier, channelType,
      url, utmCampaign, tagsRaw, dependsOnRaw, notes, weekNumberRaw,
    ] = fields.map((f) => f.trim());

    const rowErrors: string[] = [];

    // Required fields
    if (!id) rowErrors.push("id is required");
    if (!title) rowErrors.push("title is required");

    if (id && seenIds.has(id)) {
      rowErrors.push(`duplicate id "${id}"`);
    }

    if (title && title.length > 200) {
      rowErrors.push(`title exceeds 200 characters (${title.length})`);
    }

    // Enum validations
    if (!VALID_LANE_IDS.has(laneId!)) {
      rowErrors.push(`invalid laneId "${laneId}". Allowed: ${[...VALID_LANE_IDS].join(", ")}`);
    }
    if (!VALID_STATUSES.has(status!)) {
      rowErrors.push(`invalid status "${status}". Allowed: ${[...VALID_STATUSES].join(", ")}`);
    }
    if (!VALID_OWNERS.has(owner!)) {
      rowErrors.push(`invalid owner "${owner}". Allowed: ${[...VALID_OWNERS].join(", ")}`);
    }
    if (!VALID_PRIORITIES.has(priority!)) {
      rowErrors.push(`invalid priority "${priority}". Allowed: ${[...VALID_PRIORITIES].join(", ")}`);
    }

    // Optional enum validations
    if (channelTier && !VALID_TIERS.has(channelTier)) {
      rowErrors.push(`invalid channelTier "${channelTier}". Allowed: A, B, C`);
    }
    if (channelType && !VALID_CHANNEL_TYPES.has(channelType)) {
      rowErrors.push(`invalid channelType "${channelType}". Allowed: owned, rented, borrowed`);
    }

    // Date validations
    if (targetDate && !ISO_DATE_RE.test(targetDate)) {
      rowErrors.push(`invalid targetDate "${targetDate}". Must be YYYY-MM-DD`);
    }
    if (completedDate && !ISO_DATE_RE.test(completedDate)) {
      rowErrors.push(`invalid completedDate "${completedDate}". Must be YYYY-MM-DD`);
    }

    // Week number
    let weekNumber: number | undefined;
    if (weekNumberRaw) {
      const n = Number(weekNumberRaw);
      if (!Number.isInteger(n) || n < 0 || n > 4) {
        rowErrors.push(`invalid weekNumber "${weekNumberRaw}". Allowed: 0, 1, 2, 3, 4`);
      } else {
        weekNumber = n;
      }
    }

    // Multi-value fields
    const tags = tagsRaw ? tagsRaw.split(";").map((t) => t.trim()).filter(Boolean) : [];
    const dependsOn = dependsOnRaw ? dependsOnRaw.split(";").map((d) => d.trim()).filter(Boolean) : [];

    const missingV2Tags = V2_TAG_PREFIXES.filter((prefix) => !tags.some((tag) => tag.startsWith(prefix)));
    if (missingV2Tags.length > 0) {
      warnings.push(
        `Row ${rowNum} (${id || "no-id"}): missing GTM v2 tags (${missingV2Tags.join(", ")}).`,
      );
    }

    if (rowErrors.length > 0) {
      errors.push(`Row ${rowNum} (${id || "no-id"}): ${rowErrors.join("; ")}. Skipped.`);
      continue;
    }

    seenIds.add(id!);

    cards.push({
      id: id!,
      laneId: laneId as LaneId,
      title: title!,
      description: description ?? "",
      status: status as CardStatus,
      owner: owner as Owner,
      priority: priority as Priority,
      targetDate: targetDate || undefined,
      completedDate: completedDate || undefined,
      channel: channel || undefined,
      channelTier: (channelTier || undefined) as ChannelTier | undefined,
      channelType: (channelType || undefined) as ChannelType | undefined,
      url: url || undefined,
      utmCampaign: utmCampaign || undefined,
      tags,
      dependsOn,
      notes: notes ?? "",
      weekNumber,
    });
  }

  if (cards.length === 0 && errors.length === 0) {
    warnings.push("CSV has a header but no data rows.");
  }

  return { cards, errors, warnings };
}

/**
 * Read a File object and parse it as CSV.
 */
export async function importCSVFile(file: File): Promise<CSVImportResult> {
  const text = await file.text();
  return parseCSVToCards(text);
}
